import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type AudioPlayerWithSpeedProps = {
  src: string;
  label?: string;
  defaultSpeed?: number;
  showSpeedControl?: boolean;
  compact?: boolean;
  onSpeedChange?: (speed: number) => void;
  className?: string;
  style?: React.CSSProperties;
};

const SPEEDS = [0.75, 0.85, 0.9, 1.0, 1.05, 1.1, 1.15, 1.2, 1.25, 1.5];

export const AudioPlayerWithSpeed: React.FC<AudioPlayerWithSpeedProps> = ({
  src,
  label,
  defaultSpeed,
  showSpeedControl = true,
  compact = false,
  onSpeedChange,
  className,
  style,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);

  // Initialize speed from localStorage or default
  const [speed, setSpeed] = useState<number>(() => {
    if (defaultSpeed) return defaultSpeed;
    const saved = localStorage.getItem('tef_audio_preview_speed');
    if (saved) {
      const parsed = parseFloat(saved);
      if (!isNaN(parsed) && SPEEDS.includes(parsed)) {
        return parsed;
      }
    }
    return 1.0;
  });

  // Apply speed to audio element whenever it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    if (showSpeedControl) {
      localStorage.setItem('tef_audio_preview_speed', speed.toString());
    }
    if (onSpeedChange) {
      onSpeedChange(speed);
    }
  }, [speed, showSpeedControl, onSpeedChange]);

  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSpeed(parseFloat(e.target.value));
  };

  return (
    <div className={cn("flex flex-col gap-2 w-full", className)} style={style}>
      {label && <span className="text-sm font-medium text-slate-700">{label}</span>}
      <div className={cn("flex items-center gap-3", compact ? "flex-row" : "flex-col sm:flex-row")}>
        <audio
          ref={audioRef}
          src={src}
          controls
          className="w-full flex-1"
          style={compact ? { maxHeight: '30px' } : {}}
          onLoadedMetadata={() => {
            if (audioRef.current) {
              audioRef.current.playbackRate = speed;
            }
          }}
        />
        {showSpeedControl && (
          <div className="flex items-center gap-2 shrink-0 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-200">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
              {t('audio.speed')}:
            </span>
            <select
              value={speed}
              onChange={handleSpeedChange}
              className="text-xs font-semibold bg-transparent border-none text-slate-700 outline-none cursor-pointer hover:text-primary transition-colors pr-1"
              title={t('audio.playbackSpeed')}
            >
              {SPEEDS.map((s) => (
                <option key={s} value={s}>
                  {s === 1.0 ? `1.0x (${t('audio.normalSpeed')})` : `${s}x`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};
